import { DownloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import Typing from "react-typing-effect";
import { Card, Avatar, Image, Spin } from "antd";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { getMurphyPanelAsistente } from "../globals.js";

const ANIMATED_KEY = "animatedTimestamps";

const ContentChat = ({
  messages = [],
  response = [],
  files = [],
  textSize,
  isLoading,
}) => {
  const chatContainer = useRef(null);

  // Estado con timestamps animados, inicializando desde localStorage
  const [animatedSet, setAnimatedSet] = useState(() => {
    const saved = JSON.parse(localStorage.getItem(ANIMATED_KEY) || "[]");
    return new Set(saved);
  });

  // Guardar en localStorage cada vez que cambie
  useEffect(() => {
    localStorage.setItem(ANIMATED_KEY, JSON.stringify([...animatedSet]));
  }, [animatedSet]);

  // Combinar y ordenar mensajes
  const sortedData = useMemo(() => {
    const combined = [
      ...messages.map((message, index) => ({
        type: "message",
        content: message,
        index,
      })),
      ...response.map((message, index) => ({
        type: "response",
        content: message,
        index,
      })),
      ...files.map((file, index) => ({
        type: "message",
        content: file,
        index,
      })),
    ];

    return combined.sort((a, b) => {
      const timeA = new Date(a.content.timestamp);
      const timeB = new Date(b.content.timestamp);
      return timeA - timeB;
    });
  }, [messages, response, files]);

  // Índice de la última respuesta
  const lastResponseIndex = useMemo(() => {
    const responseIndexes = sortedData
      .map((item, idx) => (item.type === "response" ? idx : -1))
      .filter((idx) => idx !== -1);

    return responseIndexes.length
      ? responseIndexes[responseIndexes.length - 1]
      : -1;
  }, [sortedData]);

  // Manejar animación de la última respuesta
  useEffect(() => {
    if (lastResponseIndex === -1) return;

    const lastResponse = sortedData[lastResponseIndex];
    if (!lastResponse) return;

    const { timestamp, message } = lastResponse.content;

    // Solo animar si NO está en localStorage
    if (!animatedSet.has(timestamp)) {
      const timeout = setTimeout(() => {
        setAnimatedSet((prev) => new Set(prev).add(timestamp));
      }, message.length * 25 + 500);

      return () => clearTimeout(timeout);
    }
  }, [lastResponseIndex, sortedData, animatedSet]);

  // Scroll automático
  useEffect(() => {
    const scrollTimeout = setTimeout(() => {
      if (chatContainer.current) {
        chatContainer.current.scrollTop = chatContainer.current.scrollHeight;
      }
    }, 50);
    return () => clearTimeout(scrollTimeout);
  }, [sortedData]);

  return (
    <div
      ref={chatContainer}
      style={{
        overflowX: "hidden",
        overflowY: "scroll",
        height: "100%",
        scrollbarWidth: "thin",
        scrollbarColor: `${getMurphyPanelAsistente("bgColorGlobal")} #fff`,
      }}
    >
      {sortedData.map(({ type, content }, index) => {
        const isLastResponse =
          type === "response" && index === lastResponseIndex;
        const shouldAnimateTyping =
          isLastResponse && !animatedSet.has(content.timestamp);

        return (
          <Card
            key={`${type}-${content.timestamp}-${index}`}
            styles={{ body: { display: "flex", alignItems: "center" } }}
            style={{
              alignItems: "center",
              minHeight: "35px",
              maxWidth: "100%",
              padding: "10px",
              borderRadius: 10,
              backgroundColor:
                type === "message"
                  ? getMurphyPanelAsistente("bgColorMessage")
                  : getMurphyPanelAsistente("bgColorResponse"),
              marginTop: "10px",
              fontSize: textSize,
              fontWeight: "500",
              border: "none",
              marginRight: "10px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Avatar
              src={getMurphyPanelAsistente("logo")}
              style={{
                border: "2px solid black",
                marginRight: "10px",
                background: "#fff",
                color: "black",
                verticalAlign: "middle",
                minWidth: "15px",
                maxHeight: "15px",
                display: "inline-block",
              }}
              size={15}
            />

            {typeof content.message === "string" ? (
              <div style={{ whiteSpace: "pre-line", width: "100%" }}>
                {content.message}
              </div>
            ) : content.file instanceof File ? (
              content.file.type.startsWith("image/") ? (
                <>
                  <Image
                    src={URL.createObjectURL(content.file)}
                    alt={content.file.name}
                    style={{ width: "100px" }}
                  />
                  <a
                    href={URL.createObjectURL(content.file)}
                    download={content.file.name}
                  >
                    <button
                      style={{
                        marginLeft: "10px",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "blue",
                      }}
                    >
                      <DownloadOutlined />
                    </button>
                  </a>
                </>
              ) : (
                <>
                  <a
                    href={URL.createObjectURL(content.file)}
                    download={content.file.name}
                    style={{ color: "blue" }}
                  >
                    {content.file.name}
                    <button
                      style={{
                        marginLeft: "10px",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "blue",
                      }}
                    >
                      <DownloadOutlined />
                    </button>
                  </a>
                </>
              )
            ) : (
              <>
                <FileExcelOutlined style={{ marginRight: "5px" }} /> Archivo No
                Existente{" "}
              </>
            )}
          </Card>
        );
      })}
      {isLoading && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "10px",
            color: getMurphyPanelAsistente("titleColor"),
            fontSize: textSize,
          }}
        >
          <Spin size="small" style={{ marginRight: "8px" }} />
          <span>{getMurphyPanelAsistente("title")} is thinking...</span>
        </div>
      )}
    </div>
  );
};

export default ContentChat;
